import JSZip from 'jszip';

/** Debe coincidir con AuthContext (sessionStorage). */
const ADMIN_TOKEN_STORAGE_KEY = 'tmstudio_admin_token';

function adminAuthHeaders() {
  if (typeof sessionStorage === 'undefined') return {};
  const t = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function sanitizeFilename(value, fallback = 'archivo') {
  const base = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  return base || fallback;
}

function extensionFromUrl(url, fallbackExt = 'jpg') {
  try {
    const u = new URL(url);
    const pathname = u.pathname || '';
    const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
    if (match && match[1]) return match[1].toLowerCase();
  } catch (_) {}
  return fallbackExt;
}

function extensionFromBlob(blob, fallbackExt = 'jpg') {
  const type = (blob && blob.type) || '';
  if (type === 'image/jpeg') return 'jpg';
  if (type === 'image/png') return 'png';
  if (type === 'image/webp') return 'webp';
  if (type === 'image/gif') return 'gif';
  if (type === 'image/tiff') return 'tiff';
  return fallbackExt;
}

async function fetchImageBlob(url) {
  const res = await fetch('/api/admin/proxy-image', {
    method: 'POST',
    mode: 'same-origin',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...adminAuthHeaders(),
    },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const j = await res.json();
        if (j && j.error) msg = j.error;
      }
    } catch (_) {
      /* usar msg por defecto */
    }
    throw new Error(msg);
  }
  return res.blob();
}

function triggerBrowserDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
}

export async function downloadImageAs(url, baseName) {
  if (!url) throw new Error('URL vacía');
  const blob = await fetchImageBlob(url);
  const ext = extensionFromBlob(blob, extensionFromUrl(url));
  const safe = sanitizeFilename(baseName, 'imagen');
  triggerBrowserDownload(blob, `${safe}.${ext}`);
}

export function buildRecordPhotos(record, type) {
  if (!record) return [];
  const isSatelite = type === 'satelite';
  const items = [];
  if (isSatelite) {
    items.push({ label: 'doc-frente', url: record.photoDocFrente });
    items.push({ label: 'doc-reverso', url: record.photoDocReverso });
    items.push({ label: 'selfie-doc', url: record.photoSelfieDoc });
  }
  items.push({ label: 'rostro', url: record.photoFace });
  items.push({ label: 'cuerpo', url: record.photoBody });
  return items
    .filter((it) => it.url)
    .map((it, idx) => ({
      label: it.label,
      url: it.url,
      index: idx + 1,
    }));
}

export function buildPackageBaseName(record, type) {
  const prefix = type === 'satelite' ? 'satelite' : 'registro';
  const name = sanitizeFilename(record?.name, 'sin-nombre');
  const city = sanitizeFilename(record?.city, 'sd');
  const shortId = String(record?.id || '').replace(/-/g, '').slice(0, 6) || Date.now().toString(36);
  return `${prefix}_${name}_${city}_${shortId}`;
}

export async function downloadRecordPackage(record, type) {
  const photos = buildRecordPhotos(record, type);
  if (!photos.length) throw new Error('Este registro no tiene fotos disponibles');

  const zip = new JSZip();
  const errors = [];
  for (const { label, url, index } of photos) {
    try {
      const blob = await fetchImageBlob(url);
      const ext = extensionFromBlob(blob, extensionFromUrl(url));
      const idx = String(index).padStart(2, '0');
      zip.file(`${idx}_${label}.${ext}`, blob);
    } catch (err) {
      errors.push(`No se pudo descargar ${label}: ${err.message || err}`);
    }
  }
  if (errors.length) {
    zip.file('ERRORES.txt', errors.join('\n'));
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const base = buildPackageBaseName(record, type);
  triggerBrowserDownload(blob, `${base}.zip`);
}

export function buildImageBaseName(record, label) {
  const name = sanitizeFilename(record?.name, 'sin-nombre');
  const shortId = String(record?.id || '').replace(/-/g, '').slice(0, 6) || Date.now().toString(36);
  const safeLabel = sanitizeFilename(label, 'imagen');
  return `${name}_${safeLabel}_${shortId}`;
}
